import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Activity,
  BarChart3,
  BookOpenCheck,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock3,
  Command,
  Crosshair,
  DatabaseZap,
  FileBarChart,
  Gauge,
  Globe2,
  Home,
  Info,
  Layers3,
  LockKeyhole,
  Maximize2,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightOpen,
  Radar,
  Search,
  ServerCog,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Star,
  X,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

export interface EnterpriseTabRef {
  id: string;
  label: string;
}

type EnterpriseNavNode = {
  id?: string;
  label: string;
  caption?: string;
  icon?: LucideIcon;
  aliases?: string[];
  children?: EnterpriseNavNode[];
};

type VisibleEnterpriseNode = EnterpriseNavNode & { children?: VisibleEnterpriseNode[] };

type SearchableBlade = {
  id: string;
  label: string;
  path: string;
  primary: string;
  icon?: LucideIcon;
  aliases?: string[];
};

interface EnterpriseShellProps {
  visibleTabs: EnterpriseTabRef[];
  activeTab: string;
  onNavigate: (tabId: string) => void;
  header: ReactNode;
  customerName?: string;
  children: ReactNode;
}

const STORAGE_KEYS = {
  collapsed: 'ks-enterprise-sidebar-collapsed',
  width: 'ks-enterprise-sidebar-width',
  expandedGroups: 'ks-enterprise-sidebar-expanded-groups',
  favorites: 'ks-enterprise-favorite-blades',
  recent: 'ks-enterprise-recent-blades',
};

const ENTERPRISE_NAV: EnterpriseNavNode[] = [
  {
    label: 'Home',
    caption: 'Command center',
    icon: Home,
    children: [
      { id: 'overview', label: 'Overview', icon: Gauge },
      { id: 'mission_control', label: 'Mission Control', icon: Radar },
      { id: 'forward_operating_base', label: 'Forward Operating Base', icon: Layers3, aliases: ['fob', 'terrain', 'domain map', 'security terrain', 'operating base'] },
      { id: 'main_operating_base', label: 'Main Operating Base', icon: Layers3, aliases: ['mob', 'main operating base', 'live architecture', 'security architecture', 'security digital twin', 'digital twin'] },
      { id: 'ai_command_control', label: 'AI Command & Control Center', icon: BrainCircuit, aliases: ['ai c2', 'ai c2 mission control', 'command and control', 'command & control', 'foundry agent', 'soc l1 agent', 'soc l2 agent', 'soc l3 agent'] },
      { id: 'mssp', label: 'MSSP Overview', icon: Globe2 },
      { id: 'risk_command_center', label: 'Risk Command Center', icon: ShieldAlert },
    ],
  },
  {
    label: 'SOC Operations',
    caption: 'Triage and investigation',
    icon: ShieldCheck,
    children: [
      { id: 'incidents', label: 'Incidents', icon: ShieldAlert },
      { id: 'simulation_lab', label: 'AI Simulation Lab', icon: BrainCircuit, aliases: ['incidentops', 'ai simulation', 'simulation lab', 'investigation replay', 'training replay', 'ml handoff'] },
    ],
  },
  {
    label: 'Entity 360',
    caption: 'Entity investigation and hunting',
    icon: Activity,
    children: [
      { id: 'entity_360', label: 'Entity 360', icon: Activity },
    ],
  },
  {
    label: 'HuntOps',
    caption: 'Hunting and exposure',
    icon: Crosshair,
    children: [
      {
        label: 'Threat Hunting',
        icon: Crosshair,
        children: [
          { id: 'workbooks', label: 'Workbooks', icon: FileBarChart, aliases: ['workbooks', 'sentinel workbooks', 'interactive workbook', 'workbook studio', 'dashboard templates'] },
          { id: 'advanced_hunting', label: 'Advanced Hunting', icon: Search, aliases: ['kql', 'query', 'log analytics', 'defender hunting', 'sentinel hunting'] },
        ],
      },
      {
        label: 'Domain Impersonation',
        icon: Globe2,
        children: [
          { id: 'dnstwist', label: 'Lookalike Domain Scanner', icon: Globe2, aliases: ['domain impersonation', 'typosquat', 'homoglyph', 'phishing domain', 'domain scanner'] },
          { id: 'phishing', label: 'Phishing Overview', icon: ShieldAlert },
        ],
      },
      {
        label: 'External Exposure',
        icon: Radar,
        children: [
          { id: 'darkweb', label: 'External Attack Surface', icon: Radar },
          { id: 'spiderfoot', label: 'OSINT Reconnaissance', icon: Search, aliases: ['external exposure', 'osint', 'recon', 'attack surface'] },
        ],
      },
    ],
  },
  {
    label: 'Threat Intelligence',
    caption: 'Indicators and CVEs',
    icon: Radar,
    children: [
      { id: 'ioc', label: 'Intelligence Overview', icon: Radar, aliases: ['threat intel', 'ioc', 'indicators', 'kev', 'cve'] },
    ],
  },
  {
    label: 'Vulnerability Management',
    caption: 'Exposure and remediation',
    icon: LockKeyhole,
    children: [
      { id: 'vulnerability_mgmt', label: 'Exposure Dashboard', icon: LockKeyhole },
    ],
  },
  {
    label: 'NotebookOps / Machine Learning',
    caption: 'Notebooks and ML',
    icon: BookOpenCheck,
    children: [
      { id: 'notebook_ops', label: 'NotebookOps', icon: BookOpenCheck, aliases: ['machine learning', 'notebooks', 'jupyter', 'custom notebooks'] },
      { id: 'ml_insights', label: 'ML Insights Engine', icon: BrainCircuit },
    ],
  },
  {
    label: 'ResponseOps',
    caption: 'Automation studio',
    icon: Workflow,
    children: [
      { id: 'responseops', label: 'ResponseOps Studio', icon: Workflow },
    ],
  },
  {
    label: 'Detection & Coverage',
    caption: 'Rules and optimization',
    icon: BarChart3,
    children: [
      { id: 'coverage', label: 'MITRE Coverage', icon: BarChart3 },
      { id: 'oob_coverage_assessment', label: 'AnalyticsOps', icon: ClipboardList, aliases: ['oob rules', 'coverage assessment', 'sentinel content', 'manifest assessment', 'content hub', 'analytics rules', 'analyticsops'] },
      { id: 'custom_analytics_studio', label: 'Custom Analytics Studio', icon: Sparkles, aliases: ['custom analytics', 'custom rules', 'detection engineering', 'ai rule generation', 'custom analytics studio', 'kql generation'] },
      { id: 'detection_quality', label: 'Detection Quality', icon: Activity },
      { id: 'soc_debt', label: 'SOC Optimization', icon: Sparkles },
    ],
  },
  {
    label: 'Data & Connector Health',
    caption: 'Ingestion and operations',
    icon: DatabaseZap,
    children: [
      { id: 'connector_health', label: 'Data Connector Health', icon: DatabaseZap },
      { id: 'logs', label: 'Operational Logs', icon: ServerCog },
    ],
  },
  {
    label: 'AI & Insights',
    caption: 'Assistants and models',
    icon: BrainCircuit,
    children: [
      { id: 'soc_digital_twin', label: 'SOC Digital Twin', icon: BrainCircuit },
      { id: 'datalake', label: 'AI Assistant', icon: Sparkles },
      { id: 'security_copilot', label: 'Security Copilot', icon: Sparkles },
    ],
  },
  {
    label: 'Reports & Executive',
    caption: 'Briefings and reporting',
    icon: FileBarChart,
    children: [
      { id: 'customer_exec', label: 'Executive Portal', icon: FileBarChart },
      { id: 'reports', label: 'Reports', icon: FileBarChart },
      { id: 'report_builder', label: 'Report Builder', icon: Sparkles },
    ],
  },
  {
    label: 'Administration',
    caption: 'Governance and audit',
    icon: Settings2,
    children: [
      { id: 'masters', label: 'Administration', icon: Settings2 },
      { id: 'log_master', label: 'Audit Logs', icon: ClipboardList },
    ],
  },
];

function safeReadJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeWriteJson(key: string, value: unknown) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore storage failures */ }
}

function filterVisibleNodes(nodes: EnterpriseNavNode[], visibleIds: Set<string>): VisibleEnterpriseNode[] {
  return nodes
    .map((node) => {
      const children = node.children ? filterVisibleNodes(node.children, visibleIds) : undefined;
      const isVisibleLeaf = !!node.id && visibleIds.has(node.id);
      if (!isVisibleLeaf && (!children || children.length === 0)) return null;
      return { ...node, children } satisfies VisibleEnterpriseNode;
    })
    .filter(Boolean) as VisibleEnterpriseNode[];
}

function findActivePath(nodes: VisibleEnterpriseNode[], activeTab: string, parents: VisibleEnterpriseNode[] = []): VisibleEnterpriseNode[] {
  for (const node of nodes) {
    const nextParents = [...parents, node];
    if (node.id === activeTab) return nextParents;
    if (node.children) {
      const childPath = findActivePath(node.children, activeTab, nextParents);
      if (childPath.length) return childPath;
    }
  }
  return [];
}

function firstLeafId(node: VisibleEnterpriseNode): string | undefined {
  if (node.id) return node.id;
  for (const child of node.children ?? []) {
    const id = firstLeafId(child);
    if (id) return id;
  }
  return undefined;
}

function nodeContainsActive(node: VisibleEnterpriseNode, activeTab: string): boolean {
  if (node.id === activeTab) return true;
  return (node.children ?? []).some((child) => nodeContainsActive(child, activeTab));
}

function normalizeTabLabels(tabs: EnterpriseTabRef[]): EnterpriseTabRef[] {
  const labels: Record<string, string> = {
    spiderfoot: 'OSINT Reconnaissance',
    dnstwist: 'Lookalike Domain Scanner',
    darkweb: 'External Attack Surface',
    soc_debt: 'SOC Optimization',
    coverage: 'MITRE Coverage',
    connector_health: 'Data Connector Health',
    report_builder: 'Report Builder',
    log_master: 'Audit Logs',
    masters: 'Administration',
    notebook_ops: 'NotebookOps',
  };
  return tabs.map((tab) => ({ ...tab, label: labels[tab.id] ?? tab.label }));
}

function collectLeafIds(nodes: VisibleEnterpriseNode[]): string[] {
  return nodes.flatMap((node) => {
    const own = node.id ? [node.id] : [];
    return [...own, ...collectLeafIds(node.children ?? [])];
  });
}

function collectGroupLabels(nodes: VisibleEnterpriseNode[]): string[] {
  return nodes.flatMap((node) => {
    const own = node.children?.length ? [node.label] : [];
    return [...own, ...collectGroupLabels(node.children ?? [])];
  });
}

function flattenBlades(nodes: VisibleEnterpriseNode[], path: string[] = [], primary?: string): SearchableBlade[] {
  return nodes.flatMap((node) => {
    const nextPath = [...path, node.label];
    const nextPrimary = primary ?? node.label;
    const own = node.id
      ? [{ id: node.id, label: node.label, path: nextPath.join(' / '), primary: nextPrimary, icon: node.icon, aliases: node.aliases }]
      : [];
    return [...own, ...flattenBlades(node.children ?? [], nextPath, nextPrimary)];
  });
}

function findBladeById(blades: SearchableBlade[], id: string): SearchableBlade | undefined {
  return blades.find((blade) => blade.id === id);
}

export function EnterpriseShell({ visibleTabs, activeTab, onNavigate, header, customerName, children }: EnterpriseShellProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEYS.collapsed) === 'true';
  });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => safeReadJson<Record<string, boolean>>(STORAGE_KEYS.expandedGroups, {}));
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 320;
    const parsed = Number(localStorage.getItem(STORAGE_KEYS.width));
    return Number.isFinite(parsed) ? Math.min(480, Math.max(270, parsed)) : 330;
  });
  const [globalSearch, setGlobalSearch] = useState('');
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => safeReadJson<string[]>(STORAGE_KEYS.favorites, []));
  const [recentIds, setRecentIds] = useState<string[]>(() => safeReadJson<string[]>(STORAGE_KEYS.recent, []));
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const resizeStartRef = useRef<{ x: number; width: number } | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.collapsed, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (!collapsed) localStorage.setItem(STORAGE_KEYS.width, String(sidebarWidth));
  }, [collapsed, sidebarWidth]);

  useEffect(() => {
    safeWriteJson(STORAGE_KEYS.expandedGroups, expandedGroups);
  }, [expandedGroups]);

  useEffect(() => {
    safeWriteJson(STORAGE_KEYS.favorites, favoriteIds.slice(0, 16));
  }, [favoriteIds]);

  useEffect(() => {
    safeWriteJson(STORAGE_KEYS.recent, recentIds.slice(0, 12));
  }, [recentIds]);

  useEffect(() => {
    function handleMove(event: MouseEvent) {
      if (!resizeStartRef.current) return;
      const delta = event.clientX - resizeStartRef.current.x;
      setSidebarWidth(Math.min(480, Math.max(270, resizeStartRef.current.width + delta)));
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    function handleUp() {
      resizeStartRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  const normalizedTabs = useMemo(() => normalizeTabLabels(visibleTabs), [visibleTabs]);
  const visibleIds = useMemo(() => new Set(normalizedTabs.map((tab) => tab.id)), [normalizedTabs]);
  const visibleNav = useMemo(() => filterVisibleNodes(ENTERPRISE_NAV, visibleIds), [visibleIds]);
  const allBlades = useMemo(() => flattenBlades(visibleNav), [visibleNav]);
  const groupLabels = useMemo(() => collectGroupLabels(visibleNav), [visibleNav]);
  const activePath = useMemo(() => findActivePath(visibleNav, activeTab), [visibleNav, activeTab]);
  const activeLeaf = activePath[activePath.length - 1];
  const activePrimary = activePath[0];
  const activeBlade = useMemo(() => findBladeById(allBlades, activeTab), [allBlades, activeTab]);
  const breadcrumbPath: VisibleEnterpriseNode[] = activePath.length
    ? activePath
    : [{ label: 'Home' }, { label: 'Overview' }];

  const searchResults = useMemo(() => {
    const term = globalSearch.trim().toLowerCase();
    const activePrimaryLabel = activePrimary?.label;
    const scored = allBlades.map((blade) => {
      const haystack = `${blade.label} ${blade.path} ${blade.primary} ${(blade.aliases ?? []).join(' ')}`.toLowerCase();
      const exactBoost = term && blade.label.toLowerCase().startsWith(term) ? 30 : 0;
      const pathBoost = term && blade.path.toLowerCase().includes(term) ? 15 : 0;
      const contextBoost = activePrimaryLabel && blade.primary === activePrimaryLabel ? 12 : 0;
      const favoriteBoost = favoriteIds.includes(blade.id) ? 5 : 0;
      const recentBoost = recentIds.includes(blade.id) ? 3 : 0;
      return { blade, haystack, score: exactBoost + pathBoost + contextBoost + favoriteBoost + recentBoost };
    });
    return scored
      .filter((item) => !term || item.haystack.includes(term))
      .sort((a, b) => b.score - a.score || a.blade.label.localeCompare(b.blade.label))
      .map((item) => item.blade)
      .slice(0, 10);
  }, [activePrimary?.label, allBlades, favoriteIds, globalSearch, recentIds]);

  const favoriteBlades = useMemo(() => favoriteIds.map((id) => findBladeById(allBlades, id)).filter(Boolean) as SearchableBlade[], [favoriteIds, allBlades]);
  const recentBlades = useMemo(() => recentIds.map((id) => findBladeById(allBlades, id)).filter(Boolean) as SearchableBlade[], [recentIds, allBlades]);
  const isFavorite = favoriteIds.includes(activeTab);

  useEffect(() => {
    setActiveSearchIndex(0);
  }, [globalSearch, showSearchPanel]);

  useEffect(() => {
    if (!activePath.length) return;
    setExpandedGroups((prev) => {
      const next = { ...prev };
      for (const node of activePath.slice(0, -1)) {
        if (next[node.label] === undefined) next[node.label] = true;
      }
      return next;
    });
  }, [activePath.map((node) => node.label).join('>')]);

  useEffect(() => {
    if (!activeTab || !visibleIds.has(activeTab)) return;
    setRecentIds((prev) => [activeTab, ...prev.filter((id) => id !== activeTab)].slice(0, 8));
  }, [activeTab, visibleIds]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (collapsed) setCollapsed(false);
        setShowSearchPanel(true);
        setTimeout(() => searchInputRef.current?.focus(), 50);
      }
      if (event.key === 'Escape') {
        setShowSearchPanel(false);
        setDetailDrawerOpen(false);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [collapsed]);

  function toggleGroup(label: string) {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  function expandAllGroups() {
    setExpandedGroups(Object.fromEntries(groupLabels.map((label) => [label, true])));
  }

  function collapseAllGroups() {
    setExpandedGroups(Object.fromEntries(groupLabels.map((label) => [label, false])));
  }

  function toggleFavorite(id = activeTab) {
    if (!visibleIds.has(id)) return;
    setFavoriteIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [id, ...prev].slice(0, 16));
  }

  function navigateToBlade(id: string) {
    onNavigate(id);
    setShowSearchPanel(false);
    setGlobalSearch('');
    setActiveSearchIndex(0);
  }

  function handleSearchInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!showSearchPanel && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setShowSearchPanel(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSearchIndex((value) => Math.min(searchResults.length - 1, value + 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSearchIndex((value) => Math.max(0, value - 1));
      return;
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && searchResults.length > 0) {
      event.preventDefault();
      navigateToBlade(searchResults[Math.min(activeSearchIndex, searchResults.length - 1)].id);
    }
  }

  function renderBladeShortcut(blade: SearchableBlade, compact = false): ReactNode {
    const Icon = blade.icon ?? ChevronRight;
    const active = blade.id === activeTab;
    return (
      <button
        key={blade.id}
        type="button"
        onClick={() => navigateToBlade(blade.id)}
        className={`group flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
        title={blade.path}
      >
        <Icon size={compact ? 13 : 14} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate text-xs font-medium">{blade.label}</span>
      </button>
    );
  }

  function renderNode(node: VisibleEnterpriseNode, depth = 0): ReactNode {
    const Icon = node.icon ?? ChevronRight;
    const hasChildren = !!node.children?.length;
    const isActive = node.id === activeTab;
    const containsActive = nodeContainsActive(node, activeTab);
    const expanded = expandedGroups[node.label] ?? containsActive ?? depth === 0;
    const leafIds = collectLeafIds([node]);
    const firstId = firstLeafId(node);

    if (collapsed && depth > 0) return null;

    const baseButton = `group flex w-full items-center gap-2 rounded-lg text-left transition-colors ${collapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'} ${depth === 0 ? 'text-sm font-semibold' : 'text-xs font-medium'}`;
    const activeClass = isActive || (collapsed && containsActive)
      ? 'bg-primary text-primary-foreground shadow-sm'
      : containsActive
        ? 'bg-primary/10 text-primary dark:bg-primary/15 dark:text-sky-100'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground';

    return (
      <div key={`${node.label}-${node.id ?? leafIds.join('-')}`} className={depth > 0 && !collapsed ? 'ml-3 border-l border-border/70 pl-2' : ''}>
        <button
          type="button"
          title={node.label}
          className={`${baseButton} ${activeClass}`}
          onClick={() => {
            if (hasChildren && !collapsed && (node.children?.length ?? 0) > 1) {
              toggleGroup(node.label);
              return;
            }
            if (node.id) navigateToBlade(node.id);
            else if (firstId) navigateToBlade(firstId);
          }}
        >
          <Icon size={collapsed ? 18 : depth === 0 ? 16 : 14} className="shrink-0" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 leading-5 text-left">{node.label}</span>
              {hasChildren ? (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
            </>
          )}
        </button>
        {!collapsed && hasChildren && expanded && (
          <div className="mt-1 space-y-1">
            {node.children?.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        <aside
          className="sticky top-0 hidden h-screen shrink-0 border-r border-border bg-sidebar/95 shadow-sm transition-[width] duration-200 lg:block"
          style={{ width: collapsed ? 74 : sidebarWidth }}
          aria-label="Primary navigation"
        >
          <div className="flex h-full flex-col">
            {!collapsed && (
              <button
                type="button"
                aria-label="Resize navigation menu"
                title="Drag to resize navigation menu · Double-click to reset"
                className="absolute right-[-4px] top-0 z-20 h-full w-2 cursor-col-resize rounded-r-md bg-transparent hover:bg-primary/25 focus:outline-none focus:ring-2 focus:ring-primary/30"
                onMouseDown={(event) => {
                  resizeStartRef.current = { x: event.clientX, width: sidebarWidth };
                  event.preventDefault();
                }}
                onDoubleClick={() => setSidebarWidth(330)}
              />
            )}
            <div className={`flex h-16 items-center border-b border-border px-3 ${collapsed ? 'justify-center' : 'justify-between'}`}>
              {!collapsed && (
                <div className="min-w-0">
                  <div className="text-sm font-bold tracking-tight text-foreground">KloudSecure</div>
                  <div className="text-[11px] text-muted-foreground">Enterprise SOC Portal</div>
                </div>
              )}
              <button
                type="button"
                onClick={() => setCollapsed((value) => !value)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground"
                title={collapsed ? 'Expand menu' : 'Collapse menu'}
              >
                {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
              </button>
            </div>

            <div className="border-b border-border px-2 py-2">
              {collapsed ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground"
                    title="Global search · Ctrl+K"
                    onClick={() => {
                      setCollapsed(false);
                      setShowSearchPanel(true);
                      setTimeout(() => searchInputRef.current?.focus(), 75);
                    }}
                  >
                    <Search size={16} />
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:text-foreground"
                    title="Blade details"
                    onClick={() => setDetailDrawerOpen(true)}
                  >
                    <PanelRightOpen size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      ref={searchInputRef}
                      value={globalSearch}
                      onChange={(event) => { setGlobalSearch(event.target.value); setShowSearchPanel(true); }}
                      onKeyDown={handleSearchInputKeyDown}
                      onFocus={() => setShowSearchPanel(true)}
                      placeholder="Search blades, hunts, notebooks..."
                      className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-16 text-xs font-medium text-foreground outline-none transition focus:border-primary/60 focus:ring-2 focus:ring-primary/15"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      Ctrl K
                    </span>
                    {showSearchPanel && (
                      <div className="absolute left-0 right-0 top-[42px] z-40 rounded-xl border border-border bg-popover p-2 shadow-xl">
                        <div className="mb-2 flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          <span>Global search{activePrimary?.label ? ` · ${activePrimary.label}` : ''}</span>
                          <button type="button" onClick={() => setShowSearchPanel(false)} className="rounded p-1 hover:bg-muted"><X size={13} /></button>
                        </div>
                        {searchResults.length ? (
                          <div className="max-h-80 space-y-1 overflow-y-auto">
                            {searchResults.map((blade, index) => {
                              const Icon = blade.icon ?? ChevronRight;
                              const active = index === activeSearchIndex;
                              return (
                                <button
                                  type="button"
                                  key={blade.id}
                                  onMouseEnter={() => setActiveSearchIndex(index)}
                                  onClick={() => navigateToBlade(blade.id)}
                                  className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left ${active ? 'bg-primary/10 ring-1 ring-primary/25' : 'hover:bg-muted'}`}
                                >
                                  <Icon size={14} className={`mt-0.5 shrink-0 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate text-xs font-semibold text-foreground">{blade.label}</span>
                                    <span className="block truncate text-[11px] text-muted-foreground">{blade.path}</span>
                                    {blade.aliases?.length ? (
                                      <span className="mt-1 block truncate text-[10px] text-muted-foreground">Hints: {blade.aliases.slice(0, 4).join(', ')}</span>
                                    ) : null}
                                  </span>
                                  {active ? <span className="mt-0.5 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">Enter</span> : null}
                                </button>
                              );
                            })}
                            <div className="px-2 pb-1 pt-1 text-[10px] text-muted-foreground">Autocomplete: use ↑/↓ to select, Tab or Enter to open.</div>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">
                            No matching blades. Try another module, notebook or action name.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={expandAllGroups}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                      <Maximize2 size={12} /> Expand all
                    </button>
                    <button
                      type="button"
                      onClick={collapseAllGroups}
                      className="inline-flex items-center justify-center gap-1 rounded-lg border border-border bg-background px-2 py-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                      <Minimize2 size={12} /> Collapse all
                    </button>
                  </div>
                </>
              )}
            </div>

            <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
              {visibleNav.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">
                  <Layers3 className="mx-auto mb-2 h-5 w-5" />
                  No blades are available for your current role and customer scope.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {visibleNav.map((node) => renderNode(node))}
                </div>
              )}

              {!collapsed && favoriteBlades.length > 0 && (
                <div className="mt-5 border-t border-border pt-3">
                  <div className="mb-1.5 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Star size={12} /> Favorites
                  </div>
                  <div className="space-y-1">{favoriteBlades.slice(0, 5).map((blade) => renderBladeShortcut(blade, true))}</div>
                </div>
              )}

              {!collapsed && recentBlades.length > 0 && (
                <div className="mt-5 border-t border-border pt-3">
                  <div className="mb-1.5 flex items-center gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <Clock3 size={12} /> Recently used
                  </div>
                  <div className="space-y-1">{recentBlades.slice(0, 5).map((blade) => renderBladeShortcut(blade, true))}</div>
                </div>
              )}
            </nav>

            {!collapsed && (
              <div className="border-t border-border p-3 text-[11px] text-muted-foreground">
                <div className="rounded-xl border border-border bg-muted/30 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-foreground">Active blade</div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleFavorite(activeTab)}
                        className="rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star size={13} className={isFavorite ? 'fill-current text-amber-500' : ''} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDetailDrawerOpen(true)}
                        className="rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                        title="Open blade details"
                      >
                        <PanelRightOpen size={13} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-1 truncate">{activePrimary?.label ?? 'Home'}</div>
                  {customerName ? <div className="mt-1 truncate">Customer: {customerName}</div> : null}
                </div>
              </div>
            )}

            {!collapsed && (
              <div className="border-t border-border px-3 py-2 text-center text-[10px] text-muted-foreground">
                Property of Kloudynet &copy; {new Date().getFullYear()}
              </div>
            )}
          </div>
        </aside>

        <main className="min-w-0 flex-1">
          <div className="mx-auto w-full max-w-[1760px] px-3 py-3 sm:px-4 lg:px-5">
            {header}

            <div className="mb-3 rounded-xl border border-border bg-card p-3 shadow-sm lg:hidden">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Module</label>
              <select
                value={activeTab}
                onChange={(event) => navigateToBlade(event.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground outline-none focus:ring-2 focus:ring-primary/30"
              >
                {normalizedTabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>{tab.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-4 rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    {breadcrumbPath.map((node, index, arr) => (
                      <span key={`${node.label}-${index}`} className="inline-flex items-center gap-1">
                        <span className={index === arr.length - 1 ? 'font-semibold text-foreground' : ''}>{node.label}</span>
                        {index < arr.length - 1 ? <ChevronRight size={13} /> : null}
                      </span>
                    ))}
                  </div>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight text-foreground">
                    {activeLeaf?.label ?? 'Overview'}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleFavorite(activeTab)}
                    className="hidden h-9 items-center gap-1.5 rounded-full border border-border bg-muted/30 px-3 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground sm:inline-flex"
                  >
                    <Star size={13} className={isFavorite ? 'fill-current text-amber-500' : ''} />
                    {isFavorite ? 'Favorited' : 'Favorite'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDetailDrawerOpen(true)}
                    className="hidden h-9 items-center gap-1.5 rounded-full border border-border bg-muted/30 px-3 text-xs font-semibold text-muted-foreground hover:bg-muted hover:text-foreground sm:inline-flex"
                  >
                    <Info size={13} /> Blade details
                  </button>
                  <div className="hidden rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground md:block">
                    {activePrimary?.label ?? 'Home'}
                  </div>
                </div>
              </div>
            </div>

            {children}
          </div>
        </main>
      </div>

      {detailDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-[1px]" onClick={() => setDetailDrawerOpen(false)}>
          <aside
            className="h-full w-full max-w-md border-l border-border bg-popover text-popover-foreground shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            aria-label="Blade details"
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Blade details</div>
                <h3 className="mt-1 text-lg font-semibold text-foreground">{activeLeaf?.label ?? activeBlade?.label ?? 'Overview'}</h3>
              </div>
              <button type="button" onClick={() => setDetailDrawerOpen(false)} className="rounded-lg border border-border bg-background p-2 text-muted-foreground hover:text-foreground">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-5 overflow-y-auto px-5 py-4">
              <section className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Navigation path</div>
                <div className="mt-2 text-sm font-medium text-foreground">{activeBlade?.path ?? breadcrumbPath.map((node) => node.label).join(' / ')}</div>
                {customerName ? <div className="mt-2 text-xs text-muted-foreground">Customer context: {customerName}</div> : null}
              </section>
              <section className="rounded-xl border border-border bg-card p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Personalization</div>
                    <div className="mt-1 text-sm text-muted-foreground">Pin frequently used blades for faster access.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(activeTab)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <Star size={13} className={isFavorite ? 'fill-current text-amber-500' : ''} />
                    {isFavorite ? 'Remove' : 'Favorite'}
                  </button>
                </div>
              </section>
              <section>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Star size={12} /> Favorites</div>
                <div className="space-y-1 rounded-xl border border-border bg-card p-2">
                  {favoriteBlades.length ? favoriteBlades.slice(0, 8).map((blade) => renderBladeShortcut(blade)) : (
                    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">No favorites yet. Use the star button on any blade.</div>
                  )}
                </div>
              </section>
              <section>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Clock3 size={12} /> Recently used</div>
                <div className="space-y-1 rounded-xl border border-border bg-card p-2">
                  {recentBlades.length ? recentBlades.slice(0, 8).map((blade) => renderBladeShortcut(blade)) : (
                    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center text-xs text-muted-foreground">Recently opened blades appear here.</div>
                  )}
                </div>
              </section>
              <section className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Command size={15} /> Keyboard shortcuts</div>
                <div className="mt-2 grid gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between"><span>Global search{activePrimary?.label ? ` · ${activePrimary.label}` : ''}</span><span className="rounded border border-border bg-muted px-1.5 py-0.5 font-semibold">Ctrl / ⌘ + K</span></div>
                  <div className="flex items-center justify-between"><span>Close drawer/search</span><span className="rounded border border-border bg-muted px-1.5 py-0.5 font-semibold">Esc</span></div>
                </div>
              </section>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
